import { ITrafficSource } from '@clickster/constant-definitions/dist-esm/entities/traffic-source/types';
import { API_V2, Legacy } from '../../lib/axios';
import orderBy from 'lodash/orderBy';
import { IAd } from '@clickster/constant-definitions/dist-esm/entities/ad/types';
import { fetchAllPaginated } from '../utils/fetch-all-paginated';

const TRAFFIC_SOURCE_ENDPOINT = '/traffic-sources';

const list = async (params = {}) => {
  return fetchAllPaginated<ITrafficSource>(API_V2, TRAFFIC_SOURCE_ENDPOINT, params);
};

const find = async (id: string, params: {}) => {
  const response = await API_V2.get<ITrafficSource>(`${TRAFFIC_SOURCE_ENDPOINT}/${id}`, { params: params });

  return response.data;
};

const update = async (data: ITrafficSource) => {
  const response = await API_V2.put<ITrafficSource>(`${TRAFFIC_SOURCE_ENDPOINT}/${data._id}`, data);

  return response.data;
};

const create = async (data: ITrafficSource) => {
  const response = await API_V2.post<ITrafficSource>(`${TRAFFIC_SOURCE_ENDPOINT}`, data);

  return response.data;
};

const cpcDateRange = async (
  trafficSourceId: string,
  dates: { from: string | Date; to: string | Date; time_offset: number },
) => {
  const response = await API_V2({
    method: 'GET',
    url: `https://admin-lb.clickster.io/api/traffic-sources/${trafficSourceId}/remote_campaigns/cpc/date_range`,
    params: { r: `${Math.random()}`, ...dates },
  });
  return response.data;
};

const getHelper = async (
  helper: string,
  trafficSourceId: string,
  country?: string,
  accountId?: string,
  customParams?: Record<string, any>,
): Promise<any[]> => {
  try {
    let params = {};
    if (helper === 'audiences' || helper === 'segments') {
      params = { accountId };
    } else if (helper !== 'dma') {
      params = helper === 'cities' ? { country } : country ? { countries: [country] } : {};
    }

    const { data } = await API_V2.get(`${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/helpers/${helper}`, {
      params: { ...params, ...customParams },
    });
    return data;
  } catch (error) {
    return [];
  }
};

const postHelper = async (helper: string, trafficSourceId: string, data: any) => {
  const response = await API_V2.post(`${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/helpers/${helper}`, data);
  return response.data;
};

const getConversionEvents = async (trafficSourceId: string, accountId: string) => {
  const response = await API_V2.get(
    `${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/account/${accountId}/conversion-events`,
  );
  return response.data;
};

const getHelpersCategories = async (
  trafficSourceId: string,
): Promise<
  {
    id: string;
    name: string;
  }[]
> => {
  const response = await API_V2.get(`/${trafficSourceId}/helpers/categories`);
  return response.data;
};

const getRemoteCampaigns = async (trafficSourceId: string) => {
  const remoteCampaigns = await API_V2.get(`/${trafficSourceId}/remote-campaigns`);

  return orderBy(remoteCampaigns.data, 'name');
};

const getCallToActionOptions = async (id: string) => {
  const response = await API_V2.get(`${TRAFFIC_SOURCE_ENDPOINT}/${id}/call-to-action`, {
    params: { r: `${Math.random()}` },
  });

  // API v2 returns { data: [...] }, extract the actual array
  return response.data.data || response.data;
};

const publish = async (ads: IAd[], campaignId: string, trafficSourceId: string) => {
  const response = await Legacy.post(
    `${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/${campaignId}/ads/push`,
    JSON.stringify(ads),
    {
      headers: {
        'content-type': 'application/json',
      },
      params: { r: `${Math.random()}` },
    },
  );
  return response.data;
};

const getNextConsecutiveCampaignNumber = async (trafficSourceId: string): Promise<number> => {
  const response = await API_V2.get(`${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/next-consecutive-campaign-number`);
  return response.data;
};

/** Shape of a single Google Ads conversion action returned by the API. */
export interface IGoogleConversionAction {
  name: string;
  resource_name: string;
  status: string;
  category: string;
  type: string;
}

/**
 * Fetches Google Ads conversion actions for a given traffic source.
 *
 * @param trafficSourceId - The traffic source `_id`.
 * @returns Array of conversion actions.
 */
const getGoogleConversionActions = async (
  trafficSourceId: string,
): Promise<IGoogleConversionAction[]> => {
  const response = await API_V2.get<{ data: IGoogleConversionAction[] }>(
    `${TRAFFIC_SOURCE_ENDPOINT}/${trafficSourceId}/google-conversion-actions`,
  );
  // Support both `{ data: [...] }` and a direct array response
  return Array.isArray(response.data) ? response.data : (response.data.data ?? []);
};

export const TrafficService = {
  list,
  find,
  update,
  create,
  cpcDateRange,
  getHelper,
  getConversionEvents,
  postHelper,
  getHelpersCategories,
  getRemoteCampaigns,
  getCallToActionOptions,
  publish,
  getNextConsecutiveCampaignNumber,
  getGoogleConversionActions,
};
